<template>
  <b-modal
    v-model="show"
    title="Add Existing Domains to Stock"
    size="lg"
    @ok="handleSubmit"
    @hidden="resetForm"
    :ok-disabled="isSubmitting || (!result && domains.length === 0)"
    ok-title="Submit"
    ok-variant="success"
    cancel-variant="outline-secondary"
    no-close-on-backdrop
  >
    <vue-element-loading :active="isSubmitting" spinner="bar-fade-scale" />

    <!-- Form - only show before submission -->
    <template v-if="!result">
      <b-alert variant="info" show class="mb-3">
        <feather-icon icon="InfoIcon" size="16" class="mr-1" />
        <small>Domains will be verified with your registrar (Namecheap) before adding</small>
      </b-alert>

      <b-form-group
        label="Domain Names"
        label-for="domain-input"
        description="Enter domain names (one per line)"
      >
        <b-form-textarea
          id="domain-input"
          v-model="domainText"
          rows="8"
          placeholder="myshop1.store&#10;myshop2.store&#10;myshop3.store"
          :disabled="isSubmitting"
        />
      </b-form-group>

      <b-form-group>
        <b-form-checkbox v-model="autoSetup" :disabled="isSubmitting">
          Automatically set up in Nexus after adding
        </b-form-checkbox>
      </b-form-group>
    </template>

    <!-- Results after submission -->
    <template v-else-if="result">
      <div>
        <b-alert v-if="result.successful && result.successful.length > 0" variant="success" class="mb-2">
          <strong>✓ {{ result.successful.length }} domain(s) added successfully</strong>
          <ul class="mb-0 mt-2 small">
            <li v-for="item in result.successful" :key="item.domain">
              <strong>{{ item.domain }}</strong>
              <span v-if="item.workflowCreated" class="text-muted">
                (setup workflow created)
              </span>
            </li>
          </ul>
        </b-alert>

        <b-alert v-if="result.failed && result.failed.length > 0" variant="danger" class="mb-2">
          <strong>✗ {{ result.failed.length }} domain(s) failed</strong>
          <ul class="mb-0 mt-2 small">
            <li v-for="item in result.failed" :key="item.domain">
              <strong>{{ item.domain }}</strong>: {{ item.error }}
            </li>
          </ul>
        </b-alert>

        <b-alert v-if="(!result.successful || result.successful.length === 0) && (!result.failed || result.failed.length === 0)" variant="info">
          <strong>No results to display</strong>
        </b-alert>
      </div>
    </template>
  </b-modal>
</template>

<script>
import api from "@/services/api";

export default {
  name: "AddManualDomainModal",
  props: {
    value: {
      type: Boolean,
      required: true
    },
    slotCode: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      domainText: '',
      autoSetup: false,
      isSubmitting: false,
      result: null
    }
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    domains() {
      return this.domainText
        .split('\n')
        .map(d => d.trim())
        .filter(d => d.length > 0)
    }
  },
  methods: {
    closeModal() {
      this.$emit('input', false)
    },
    async handleSubmit(evt) {
      // If results are already shown, just close the modal
      if (this.result) {
        evt.preventDefault()
        this.closeModal()
        return
      }

      // Prevent default modal close behavior for form submission
      evt.preventDefault()

      if (!this.slotCode) {
        this.showToast('error', 'Slot code is required')
        return
      }

      if (this.domains.length === 0) {
        this.showToast('error', 'Please enter at least one domain')
        return
      }

      this.isSubmitting = true
      this.result = null

      try {
        const response = await api.post('/domainstock/manual', {
          slotCode: this.slotCode,
          domains: this.domains,
          autoSetup: this.autoSetup
        })

        // Ensure result has proper structure
        this.result = {
          successful: response.successful || [],
          failed: response.failed || []
        }

        const successCount = this.result.successful.length
        const failCount = this.result.failed.length
        const totalCount = successCount + failCount

        // Emit success event if any domains were processed
        if (successCount > 0) {
          this.$emit('success', this.result)
        }

        // Show appropriate toast based on results
        if (totalCount === 0) {
          // No domains processed at all
          this.showToast('warning', 'No domains were processed')
        } else if (successCount > 0 && failCount === 0) {
          // All domains succeeded
          this.showToast('success', `${successCount} domain(s) added successfully`)
        } else if (successCount === 0 && failCount > 0) {
          // All domains failed
          this.showToast('error', `${failCount} domain(s) failed to add`)
        } else {
          // Mixed results - some success, some failed
          this.showToast('warning', `${successCount} succeeded, ${failCount} failed`)
        }
      } catch (error) {
        const errorMessage = error.response?.data || error.message || 'Failed to add domains'
        this.showToast('error', errorMessage)
      } finally {
        this.isSubmitting = false
        this.closeModal()
      }
    },
    resetForm() {
      this.domainText = ''
      this.autoSetup = false
      this.result = null
      this.isSubmitting = false
    }
  }
}
</script>
